import useSWR, { preload } from 'swr';
import {
  API_GET_USER_PREFERENCES,
  API_PUT_USER_PREFERENCES
} from '../api/configurationScreenServices';

/**
 * Centralized User Preferences Hook using SWR
 *
 * SWR handles:
 * - Automatic caching (single API call shared across all components)
 * - Deduplication (multiple usePreferences() calls = 1 API request)
 * - Automatic revalidation
 *
 * Usage:
 * const { preferences, isLoading, updatePreference } = usePreferences();
 * updatePreference({ casaWebDashboardCoachMarksVisited: true });
 */

const PREFERENCES_KEY = 'user-preferences';

const fetcher = async () => {
  const response = await API_GET_USER_PREFERENCES();
  if (response?.status !== 200) {
    throw new Error('Failed to fetch user preferences');
  }
  return response.data;
};

/**
 * Warm the SWR cache at app boot (replaces the old Redux
 * apiGetUserPreferences critical-meta thunk). Rejects on failure so
 * callers can gate initialization on it.
 */
export const preloadPreferences = () => preload(PREFERENCES_KEY, fetcher);

export function usePreferences() {
  const { data, error, isLoading, mutate } = useSWR(PREFERENCES_KEY, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60000 // 1 minute - prevent duplicate calls
  });

  /**
   * Update preferences
   * - Updates local cache immediately (optimistic)
   * - Calls PUT API in background
   */
  const updatePreference = async (updates) => {
    // Optimistic update - update local state immediately
    mutate(
      { ...data, ...updates },
      false // Don't revalidate (no refetch needed)
    );

    // Call PUT API to update backend
    try {
      await API_PUT_USER_PREFERENCES(updates);
    } catch (error) {
      // Rollback on error
      mutate(data, false);
      console.error('Failed to update preferences:', error);
      throw error;
    }
  };

  return {
    preferences: data,
    isLoading,
    error,
    updatePreference
  };
}

/**
 * Convenience hook for single preference
 *
 * Usage:
 * const [visited, setVisited] = usePreference('casaWebDashboardCoachMarksVisited');
 */
export function usePreference(key, defaultValue = false) {
  const { preferences, updatePreference } = usePreferences();

  const value = preferences?.[key] ?? defaultValue;
  const setValue = (newValue) => updatePreference({ [key]: newValue });

  return [value, setValue];
}
